<template>
    <div class="pa-2">
        <div class="d-flex align-center flex-wrap mb-2" style="gap: 8px">
            <v-text-field v-model="query" label="Search (name / ID / /regex/)"
                :error="!!searchError" :error-messages="searchError"
                :hide-details="!searchError" density="compact" clearable
                style="max-width: 300px" />
            <v-autocomplete v-if="!anonymizeMode" v-model="entityFilter" :items="entityOptions" label="Character" hide-details
                density="compact" clearable multiple chips closable-chips style="min-width: 200px; max-width: 320px" />
            <v-autocomplete v-if="!anonymizeMode" v-model="masterFilter" :items="masterOptions" label="Owner" hide-details
                density="compact" clearable multiple chips closable-chips style="min-width: 180px; max-width: 300px" />
            <v-autocomplete v-model="storageFilter" :items="storageOptions" label="Storage" hide-details
                density="compact" clearable multiple chips closable-chips style="min-width: 140px; max-width: 240px" />
            <v-btn :loading="loading" @click="reload">Refresh</v-btn>
            <v-menu :close-on-content-click="false">
                <template #activator="{ props }">
                    <v-btn v-bind="props" icon="mdi-view-column" size="small" variant="text" title="Columns" />
                </template>
                <v-list density="compact">
                    <v-list-item v-for="h in allHeaders" :key="h.key" class="py-0">
                        <v-checkbox v-model="visibleCols" :label="h.title" :value="h.key"
                            hide-details density="compact" />
                    </v-list-item>
                </v-list>
            </v-menu>
            <v-btn size="small" variant="text" prepend-icon="mdi-file-delimited-outline"
                @click="exportCsv">Export CSV</v-btn>
            <span class="text-caption text-medium-emphasis">{{ entityCount }} characters / {{ itemKindCount }} item types</span>
        </div>
        <v-alert v-if="loadError" type="error" variant="tonal" density="compact" class="mb-2">{{ loadError }}</v-alert>
        <v-alert v-else-if="!loading && !holders.length" type="info" variant="tonal" density="compact" class="mb-2">
            No items recorded yet. Inventories are captured when a character logs in or changes channel;
            open the bank or beauty room to record those too.
        </v-alert>
        <div v-if="excludes.length" class="d-flex align-center flex-wrap mb-2" style="gap: 6px">
            <span class="text-caption text-medium-emphasis">Excluded:</span>
            <v-chip v-for="(e, i) in excludes" :key="`${e.col}:${e.value}`" size="small"
                closable prepend-icon="mdi-minus-circle-outline"
                @click:close="removeExclude(i)">{{ e.value }}</v-chip>
            <v-btn size="x-small" variant="text" @click="clearExcludes">Clear all</v-btn>
        </div>
        <v-data-table :headers="headers" :items="rows" v-model:sort-by="sortBy" density="compact"
            :items-per-page="50">
            <template #[`item.item`]="{ item }">
                <span class="idx-cell">
                    <v-tooltip v-if="item.tip" location="right" content-class="item-tip-content" :open-delay="150">
                        <template #activator="{ props }">
                            <span v-bind="props" class="item-name-hover">{{ item.item }}</span>
                        </template>
                        <item-tip :title="item.item" :tip="item.tip" />
                    </v-tooltip>
                    <span v-else>{{ item.item }}</span>
                    <v-icon class="idx-exclude" icon="mdi-minus-circle-outline" size="x-small"
                        title="Exclude" @click.stop="addExclude('item', item.item)" />
                </span>
            </template>
            <template #[`item.entity`]="{ item }">
                <span class="idx-cell">
                    {{ item.entity }}
                    <v-icon class="idx-exclude" icon="mdi-minus-circle-outline" size="x-small"
                        title="Exclude" @click.stop="addExclude('entity', item.entity)" />
                </span>
            </template>
            <template #[`item.storage`]="{ item }">
                <span class="idx-cell">
                    {{ item.storage }}
                    <v-icon class="idx-exclude" icon="mdi-minus-circle-outline" size="x-small"
                        title="Exclude" @click.stop="addExclude('storage', item.storage)" />
                </span>
            </template>
        </v-data-table>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from 'vue';
import {
    flattenIndex, parseSearchQuery, parseExcludeEntries, buildExcludeSets, isExcludeEmpty,
    type IndexEntity, type Holder, type ExcludeColumn, type ExcludeEntry,
} from '@/items/itemIndex';
import { buildTip, pocketName, enchantText, metalwareText } from '@/items/itemTooltip';
import { itemName, ensureItemNames } from '@/items/itemNames';
import { buildCsv, downloadCsv, sortRows, type SortSpec } from '@/items/csvExport';
import { anonymizeMode } from '@/store';
import ItemTip from './subComponents/itemTip.vue';

// Storage codes from the item store; unknown codes show as-is.
const STORAGE_NAMES: Record<string, string> = {
    inventory: 'Inventory',
    beauty: 'Beauty Room',
    bank: 'Bank',
};

// Items tab: every item of every character seen in a 0x5209 snapshot, plus
// bank and beauty-room contents, from /api/item-index. Names come from the
// funnyresources item API.
export default defineComponent({
    components: { ItemTip },
    setup() {
        const query = ref('');
        const loading = ref(false);
        const loadError = ref('');
        const holders = ref<Holder[]>([]);

        const entityFilter = ref<string[]>([]);
        const masterFilter = ref<string[]>([]);
        const storageFilter = ref<string[]>([]);
        // Current v-data-table sort, so CSV export matches the screen.
        const sortBy = ref<SortSpec[]>([]);

        const storageText = (h: Holder): string => STORAGE_NAMES[h.storage] ?? h.storage;

        // containerText: bag/tab name > bag item name > known pocket name > category.
        const containerText = (h: Holder): string => {
            // Bank tabs are named after characters, so anonymize mode hides them too.
            if (h.bagName) return h.storage === 'bank' && anonymizeMode.value ? 'Bank tab' : h.bagName;
            if (h.bagItemId) return itemName(h.bagItemId);
            const tab = pocketName(h.pocket ?? 0);
            if (tab) return tab;
            if (h.container === 'quest') return 'Quest';
            if (h.container === 'bag') return `Unknown pocket #${h.pocket ?? '?'}`;
            return h.container;
        };

        const reload = async () => {
            loading.value = true;
            loadError.value = '';
            try {
                const r = await fetch('/api/item-index');
                if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
                const data: IndexEntity[] = await r.json();
                holders.value = flattenIndex(data);
                const ids = new Set<number>();
                for (const h of holders.value) {
                    ids.add(h.id);
                    if (h.bagItemId) ids.add(h.bagItemId);
                }
                // Names arrive in the background; the table fills them in as they land.
                void ensureItemNames(ids);
            } catch (e) {
                loadError.value = `Failed to load the item index: ${(e as Error).message}`;
            } finally {
                loading.value = false;
            }
        };

        const entityCount = computed(() => new Set(holders.value.map(h => h.entity)).size);
        const itemKindCount = computed(() => new Set(holders.value.map(h => h.id)).size);

        // Text search matches the item name, enchant ids and metalware ids.
        const searchTextCache = computed(() => {
            const m = new Map<Holder, string>();
            for (const h of holders.value) {
                m.set(h, `${itemName(h.id)} ${enchantText(h)} ${metalwareText(h)}`.toLowerCase());
            }
            return m;
        });

        const distinct = (pick: (h: Holder) => string): string[] => {
            const set = new Set<string>();
            for (const h of holders.value) {
                const v = pick(h);
                if (v) set.add(v);
            }
            return [...set].sort((a, b) => a.localeCompare(b));
        };
        const entityOptions = computed(() => distinct(h => h.entity));
        const masterOptions = computed(() => distinct(h => h.master));
        // Same display strings as the table cells, so the dropdown never
        // disagrees with the Storage column.
        const storageOptions = computed(() => distinct(storageText));

        // Exclude list: "never show these", complementing the filters' "only show these".
        const EXCLUDES_STORAGE_KEY = 'funnycharts.itemIndexExcludes.v1';
        const loadExcludes = (): ExcludeEntry[] => {
            try { return parseExcludeEntries(localStorage.getItem(EXCLUDES_STORAGE_KEY)); } catch { return []; }
        };
        const excludes = ref<ExcludeEntry[]>(loadExcludes());
        watch(excludes, v => {
            try { localStorage.setItem(EXCLUDES_STORAGE_KEY, JSON.stringify(v)); } catch { /* ignore */ }
        }, { deep: true });
        const excludeSets = computed(() => buildExcludeSets(excludes.value));

        const addExclude = (col: ExcludeColumn, value: string) => {
            if (!value) return;
            if (excludes.value.some(e => e.col === col && e.value === value)) return;
            excludes.value = [...excludes.value, { col, value }];
        };
        const removeExclude = (i: number) => {
            excludes.value = excludes.value.filter((_, j) => j !== i);
        };
        const clearExcludes = () => { excludes.value = []; };

        const searchQuery = computed(() => parseSearchQuery(query.value ?? ''));
        const searchError = computed(() =>
            searchQuery.value.kind === 'error' ? searchQuery.value.message : '');

        const rows = computed(() => {
            const sq = searchQuery.value;
            if (sq.kind === 'error') return [];
            let list = holders.value;
            const cache = searchTextCache.value;
            if (sq.kind === 'id') {
                list = list.filter(h => h.id === sq.id);
            } else if (sq.kind === 'text') {
                list = list.filter(h => (cache.get(h) ?? '').includes(sq.needle));
            } else if (sq.kind === 'regex') {
                list = list.filter(h => sq.re.test(cache.get(h) ?? ''));
            }
            if (entityFilter.value.length) list = list.filter(h => entityFilter.value.includes(h.entity));
            if (masterFilter.value.length) list = list.filter(h => masterFilter.value.includes(h.master));
            if (storageFilter.value.length) list = list.filter(h => storageFilter.value.includes(storageText(h)));
            const ex = excludeSets.value;
            if (!isExcludeEmpty(ex)) {
                list = list.filter(h =>
                    !ex.entity.has(h.entity)
                    && !ex.storage.has(storageText(h))
                    && !ex.item.has(itemName(h.id)));
            }
            return list.map(h => ({
                item: itemName(h.id),
                itemId: h.id,
                enchant: enchantText(h),
                metalware: metalwareText(h),
                entity: h.entity,
                master: h.master,
                storage: storageText(h),
                container: containerText(h),
                pocket: h.pocket ?? '',
                qty: h.qty,
                pos: `(${h.x},${h.y})`,
                tip: buildTip(h),
            }));
        });

        type IndexRow = (typeof rows.value)[number];
        // Every row field is already the display-ready cell value.
        const cellText = (row: IndexRow, key: string): string => {
            const v = (row as unknown as Record<string, unknown>)[key];
            return v == null ? '' : String(v);
        };

        // Column visibility persists in localStorage; artisan and pocket are hidden by default.
        const allHeaders = [
            { title: 'Item', key: 'item' },
            { title: 'Item ID', key: 'itemId' },
            { title: 'Enchants', key: 'enchant' },
            { title: 'Artisan', key: 'metalware' },
            { title: 'Character', key: 'entity' },
            { title: 'Owner', key: 'master' },
            { title: 'Storage', key: 'storage' },
            { title: 'Bag', key: 'container' },
            { title: 'Pocket', key: 'pocket' },
            { title: 'Qty', key: 'qty' },
            { title: 'Position', key: 'pos' },
        ];
        const COLS_STORAGE_KEY = 'funnycharts.itemIndexCols.v1';
        const defaultCols = allHeaders.map(h => h.key).filter(k => k !== 'metalware' && k !== 'pocket');
        const loadCols = (): string[] => {
            try {
                const raw = localStorage.getItem(COLS_STORAGE_KEY);
                if (raw) {
                    const valid = (JSON.parse(raw) as string[]).filter(k => allHeaders.some(h => h.key === k));
                    if (valid.length) return valid;
                }
            } catch { /* ignore */ }
            return [...defaultCols];
        };
        const visibleCols = ref<string[]>(loadCols());
        watch(visibleCols, v => {
            try { localStorage.setItem(COLS_STORAGE_KEY, JSON.stringify(v)); } catch { /* ignore */ }
        }, { deep: true });
        // Anonymize mode hides character names, like the damage tabs hide the Player column.
        const headers = computed(() => allHeaders.filter(h => visibleCols.value.includes(h.key)
            && !(anonymizeMode.value && (h.key === 'entity' || h.key === 'master'))));

        // exportCsv dumps exactly what is on screen: visible columns x filtered rows, in sort order.
        const exportCsv = () => {
            const cols = headers.value;
            const sorted = sortRows(rows.value, sortBy.value);
            const csv = buildCsv(cols.map(h => h.title), sorted.map(r => cols.map(h => cellText(r, h.key))));
            const now = new Date();
            const pad = (n: number) => String(n).padStart(2, '0');
            const ts = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`
                + `-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
            downloadCsv(`items_${ts}.csv`, csv);
        };

        onMounted(reload);
        return {
            query, loading, loadError, holders, reload, rows, headers, allHeaders, visibleCols,
            entityCount, itemKindCount,
            entityFilter, masterFilter, storageFilter,
            entityOptions, masterOptions, storageOptions,
            sortBy, exportCsv, searchError,
            excludes, addExclude, removeExclude, clearExcludes,
            anonymizeMode,
        };
    },
});
</script>

<style>
.item-name-hover {
    cursor: help;
    border-bottom: 1px dotted #777;
}

/* Exclude button: hidden until the cell is hovered. */
.idx-cell {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.idx-cell .idx-exclude {
    opacity: 0;
    cursor: pointer;
    transition: opacity 0.12s;
}

.idx-cell:hover .idx-exclude {
    opacity: 0.6;
}

.idx-cell .idx-exclude:hover {
    opacity: 1;
    color: #ef5350;
}
</style>
